import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

type ActionItem = { id: string; text: string; assignee: string; dueDate: string | null; status: string; confidence: number; source: string };
type TranscriptSeg = { time: string; speaker: string; text: string };
type Attendee = { name: string; status: string };

const MEETINGS: Array<{
  id: string; title: string; date: string; time: string; duration: string; type: string; status: string;
  organizer: string; attendees: Attendee[]; agenda: string[];
  videoLink: string; recording: boolean; transcription: boolean;
  actionItems: ActionItem[]; transcript: TranscriptSeg[]; notes: string;
}> = [];

const statusVariant: Record<string, 'success' | 'primary' | 'warning' | 'default'> = {
  'in-progress': 'success', scheduled: 'primary', completed: 'default', cancelled: 'warning',
};
const attendeeVariant: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  accepted: 'success', tentative: 'warning', declined: 'error', pending: 'default',
};
const typeIcon: Record<string, string> = {
  video: '', phone: '', 'in-person': '', hybrid: '',
};

export function MeetingsCalendar() {
  const [selectedMeeting, setSelectedMeeting] = useState<typeof MEETINGS[0] | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'recording' | 'transcription' | 'action-items' | 'notes'>('overview');
  const [showCreate, setShowCreate] = useState(false);
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [activeView, setActiveView] = useState<'my' | 'library' | 'action-items'>('my');

  const today = MEETINGS.filter(m => m.date === '2026-03-09');
  const upcoming = MEETINGS.filter(m => m.date !== '2026-03-09');

  const allActionItems = MEETINGS.flatMap(m => m.actionItems.map(ai => ({ ...ai, meeting: m.title, meetingDate: m.date })));

  if (showCreate) {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="unstyled" size="unstyled" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-primary transition-colors font-semibold">← Back</Button>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Schedule Meeting</h1>
        </div>
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Meeting Title *</label>
              <Input placeholder="e.g. Weekly Team Sync" className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Description / Agenda</label>
              <textarea rows={4} placeholder="Add meeting agenda items..." className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none resize-none" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Date *</label>
                <Input type="date" defaultValue="2026-03-10" className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Start Time *</label>
                <Input type="time" defaultValue="10:00" className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Duration</label>
                <select className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  {['15m', '30m', '45m', '1h', '1.5h', '2h', '3h'].map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Meeting Type</label>
                <select className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  <option>Video (Google Meet)</option>
                  <option>Video (Zoom)</option>
                  <option>Video (Teams)</option>
                  <option>Phone</option>
                  <option>In-Person</option>
                  <option>Hybrid</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Recurrence</label>
                <select className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  <option>None (one-time)</option>
                  <option>Daily</option>
                  <option>Weekly</option>
                  <option>Bi-weekly</option>
                  <option>Monthly</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Invite Participants</label>
              <Input placeholder="Search employees or enter email..." className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none" />
            </div>
            <div className="p-4 bg-card border border-border rounded-lg space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Meeting Options</p>
              <div className="flex flex-wrap gap-4">
                {[
                  { label: 'Auto-generate transcript', key: 'transcript' },
                  { label: 'Record meeting', key: 'record' },
                  { label: 'Send reminders (15m before)', key: 'reminders' },
                  { label: 'Extract AI action items', key: 'ai' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" defaultChecked className="w-4 h-4 accent-primary" />
                    <span className="text-sm text-foreground">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button variant="primary" className="flex-1" onClick={() => { toast.success('Meeting scheduled! Invites sent to all participants.'); setShowCreate(false); }}>
                Schedule & Send Invites
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedMeeting) {
    const filteredTranscript = selectedMeeting.transcript.filter(s =>
      !transcriptSearch || s.text.toLowerCase().includes(transcriptSearch.toLowerCase()) || s.speaker.toLowerCase().includes(transcriptSearch.toLowerCase())
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="unstyled" size="unstyled" onClick={() => setSelectedMeeting(null)} className="hover:text-primary transition-colors font-semibold">Meetings</Button>
          <span>/</span>
          <span className="text-foreground font-semibold">{selectedMeeting.title}</span>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <Badge variant={statusVariant[selectedMeeting.status]}>{selectedMeeting.status === 'in-progress' ? 'Live' : selectedMeeting.status}</Badge>
                  <span className="text-lg">{typeIcon[selectedMeeting.type]}</span>
                  <span className="text-xs text-muted-foreground font-mono">{selectedMeeting.date} · {selectedMeeting.time} · {selectedMeeting.duration}</span>
                </div>
                <h1 className="tracking-tight text-foreground font-display text-page-title">{selectedMeeting.title}</h1>
                <p className="text-sm text-muted-foreground mt-1">Organized by {selectedMeeting.organizer} · {selectedMeeting.attendees.length} attendees</p>
              </div>
              <div className="flex gap-2">
                {selectedMeeting.videoLink && (
                  <Button variant="primary" size="sm" onClick={() => toast.success('Joining meeting...')}>Join Meeting</Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => toast.success('Meeting edit...')}>Edit</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            ...(selectedMeeting.recording ? [{ id: 'recording', label: 'Recording' }] : []),
            ...(selectedMeeting.transcription ? [{ id: 'transcription', label: 'Transcription' }] : []),
            { id: 'action-items', label: `Action Items (${selectedMeeting.actionItems.length})` },
            { id: 'notes', label: 'Notes' },
          ].map(t => (
            <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
              className={`pb-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              {t.label}
              {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
            </Button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Meeting Agenda</h2></CardHeader>
                <CardContent className="p-6">
                  <ol className="space-y-3">
                    {selectedMeeting.agenda.map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</span>
                        <span className="text-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
              {selectedMeeting.actionItems.length > 0 && (
                <Card>
                  <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                    <h2 className="text-foreground font-display text-section-title">Action Items Preview</h2>
                    <Button variant="secondary" size="sm" onClick={() => setActiveTab('action-items')}>View All →</Button>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2">
                    {selectedMeeting.actionItems.slice(0, 2).map(ai => (
                      <div key={ai.id} className={`flex items-center gap-3 p-3 rounded-lg border ${ai.status === 'completed' ? 'bg-success/5 border-success/20 opacity-70' : 'bg-card border-border'}`}>
                        <span className="text-lg">{ai.status === 'completed' ? '' : ''}</span>
                        <div className="flex-1">
                          <p className={`text-sm ${ai.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'} font-semibold`}>{ai.text}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{ai.assignee}{ai.dueDate ? ` · Due: ${ai.dueDate}` : ''}</p>
                        </div>
                        <Badge variant={ai.status === 'completed' ? 'success' : ai.status === 'accepted' ? 'primary' : 'default'}>{ai.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="space-y-4">
              <Card>
                <CardHeader className="bg-card/30 px-4 py-3"><p className="text-xs font-semibold text-muted-foreground">Attendees ({selectedMeeting.attendees.length})</p></CardHeader>
                <CardContent className="p-4 space-y-2">
                  {selectedMeeting.attendees.map((a, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-xs font-semibold text-primary">{a.name[0]}</div>
                        <p className="text-xs font-semibold text-foreground">{a.name}</p>
                      </div>
                      <Badge variant={attendeeVariant[a.status]}>{a.status}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 space-y-2">
                  {selectedMeeting.videoLink && (
                    <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/20 rounded-lg">
                      <span className="text-sm"></span>
                      <span className="font-mono text-xs text-success">{selectedMeeting.videoLink}</span>
                    </div>
                  )}
                  {selectedMeeting.recording && <p className="text-xs text-success font-semibold">Recording enabled</p>}
                  {selectedMeeting.transcription && <p className="text-xs text-primary font-semibold">AI transcription enabled</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'action-items' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-muted-foreground">AI-Extracted Action Items · Confidence scores shown</p>
              <Button variant="primary" size="sm" onClick={() => toast.success('Adding manual action item...')}>+ Add Manually</Button>
            </div>
            {selectedMeeting.actionItems.map(ai => (
              <Card key={ai.id} className={`border-l-4 ${ai.status === 'completed' ? 'border-l-success' : ai.status === 'accepted' ? 'border-l-primary-500' : 'border-l-border-12'}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-xl mt-0.5">{ai.status === 'completed' ? '' : ai.status === 'accepted' ? '' : ''}</span>
                      <div className="flex-1">
                        <p className={`font-semibold text-sm ${ai.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{ai.text}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">{ai.assignee}</span>
                          {ai.dueDate && <span className="text-xs text-muted-foreground">Due: {ai.dueDate}</span>}
                          <span className="text-xs text-muted-foreground">{Math.round(ai.confidence * 100)}% confidence</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 border-l-2 border-border pl-2">"{ai.source}"</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {ai.status === 'pending' && (
                        <>
                          <Button variant="primary" size="sm" onClick={() => toast.success('Action item accepted!')}>Accept</Button>
                          <Button variant="secondary" size="sm" onClick={() => toast.success('Action item rejected.')}>Reject</Button>
                        </>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => toast.success('Editing...')}>Edit</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {activeTab === 'transcription' && (
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
              <h2 className="text-foreground font-display text-section-title">Meeting Transcription</h2>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => toast.success('Downloading PDF...')}>PDF</Button>
                <Button variant="secondary" size="sm" onClick={() => toast.success('Downloading DOC...')}>Word</Button>
                <Button variant="secondary" size="sm" onClick={() => toast.success('Copied!')}>Copy</Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Input value={transcriptSearch} onChange={e => setTranscriptSearch(e.target.value)} placeholder="Search transcription..."
                className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <div className="space-y-4">
                {filteredTranscript.map((seg, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="font-mono text-xs text-muted-foreground w-12 shrink-0 pt-1">[{seg.time}]</span>
                    <div>
                      <p className="text-xs font-semibold text-primary mb-1">{seg.speaker}</p>
                      <p className="text-sm text-foreground leading-relaxed">{seg.text}</p>
                    </div>
                  </div>
                ))}
                {filteredTranscript.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No results for "{transcriptSearch}"</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'notes' && (
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
              <h2 className="text-foreground font-display text-section-title">Meeting Notes</h2>
              <Button variant="primary" size="sm" onClick={() => toast.success('Notes saved!')}>Save</Button>
            </CardHeader>
            <CardContent className="p-6">
              <textarea defaultValue={selectedMeeting.notes} rows={12}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
                placeholder="Add meeting notes here (Markdown supported)..." />
            </CardContent>
          </Card>
        )}

        {activeTab === 'recording' && (
          <Card><CardContent className="p-12 text-center">
            <div className="w-24 h-24 mx-auto rounded-lg bg-destructive/10 border border-destructive/30 flex items-center justify-center text-4xl mb-4"></div>
            <p className="font-semibold text-foreground mb-2">Recording Available</p>
            <p className="text-sm text-muted-foreground mb-4">Duration: {selectedMeeting.duration}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="primary" onClick={() => toast.success('Playing recording...')}>▶ Play Recording</Button>
              <Button variant="secondary" onClick={() => toast.success('Downloading recording...')}>Download</Button>
            </div>
          </CardContent></Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Meeting <span className="text-foreground">Intelligence</span></h1>
          <p className="text-muted-foreground mt-1">Scheduler · Transcriptions · AI Action Items · Library</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>+ Schedule Meeting</Button>
      </div>

      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'my', label: 'My Meetings' },
          { id: 'library', label: 'Meeting Library' },
          { id: 'action-items', label: `All Action Items (${allActionItems.length})` },
        ].map(t => (
          <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveView(t.id as any)}
            className={`pb-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${activeView === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {activeView === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </Button>
        ))}
      </div>

      {(activeView === 'my' || activeView === 'library') && (
        <div className="space-y-6">
          {activeView === 'my' && today.length > 0 && (
            <div>
              <h2 className="text-muted-foreground mb-3 font-display text-card-title">Today — Mar 9, 2026</h2>
              <div className="space-y-3">
                {today.map(m => (
                  <Card key={m.id} className={`cursor-pointer hover:-translate-y-0.5 transition-all group border-l-4 ${m.status === 'in-progress' ? 'border-l-success' : 'border-l-primary-500'}`} onClick={() => setSelectedMeeting(m)}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="font-semibold text-primary text-sm">{m.time}</p>
                            <p className="text-xs text-muted-foreground">{m.duration}</p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{typeIcon[m.type]}</span>
                              <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{m.title}</p>
                              <Badge variant={statusVariant[m.status]}>{m.status === 'in-progress' ? 'Live' : m.status}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">Organized by {m.organizer} · {m.attendees.length} attendees</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {m.videoLink && m.status !== 'completed' && (
                            <Button variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); toast.success('Editing event...'); }}>Edit</Button>

                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-muted-foreground mb-3 font-display text-card-title">Upcoming</h2>
            <div className="space-y-3">
              {(activeView === 'my' ? upcoming : MEETINGS).map(m => (
                <Card key={m.id} className="cursor-pointer hover:-translate-y-0.5 transition-all group" onClick={() => setSelectedMeeting(m)}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[80px] bg-card border border-border rounded-lg p-2">
                          <p className="text-xs text-muted-foreground">{m.date}</p>
                          <p className="font-semibold text-primary text-sm">{m.time}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span>{typeIcon[m.type]}</span>
                            <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{m.title}</p>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{m.organizer}</span>
                            <span>· {m.attendees.length} attendees</span>
                            <span>· {m.duration}</span>
                            {m.transcription && <span className="text-primary">AI Transcript</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeView === 'action-items' && (
        <div className="space-y-3">
          {allActionItems.map((ai, i) => (
            <Card key={i} className={`border-l-4 ${ai.status === 'completed' ? 'border-l-success opacity-70' : ai.status === 'accepted' ? 'border-l-primary-500' : 'border-l-border-12'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{ai.status === 'completed' ? '' : ''}</span>
                    <div>
                      <p className={`text-sm font-semibold ${ai.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{ai.text}</p>
                      <p className="text-xs text-muted-foreground mt-1">{ai.meeting} · {ai.assignee}{ai.dueDate ? ` · Due: ${ai.dueDate}` : ''}</p>
                    </div>
                  </div>
                  <Badge variant={ai.status === 'completed' ? 'success' : ai.status === 'accepted' ? 'primary' : 'default'}>{ai.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
